import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useApi } from './ApiContext';
import './Dashboard.css';

// Import all dashboard components
import Analytics from './dashboard/Analytics';
import ManageCode from './dashboard/ManageCode';
import ViewOrder from './dashboard/ViewOrder';
import Settings from './dashboard/Settings';
import Support from './dashboard/Support';
import ManageProduct from './dashboard/ManageProduct';
import ManageDownload from './dashboard/ManageDownload';
import ManageUser from './dashboard/ManageUser';
import Maintenance from './dashboard/Maintenance'; // ➡️ Import Maintenance
import WhatsNew from './dashboard/WhatsNew';

const Dashboard = () => {
  const { api } = useApi();
  const { isAuthenticated, username, loading, userType, setIsAuthenticated, setUsername, setEmail, setUserType } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activePage, setActivePage] = useState(null);

  const sidebarRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const fabButtonRef = useRef(null);
  const hamburgerRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (isAuthenticated === false) {
      history.push('/');
    }
  }, [isAuthenticated, loading, history]);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');
    if (pageParam) {
      setActivePage(pageParam);
    } else {
      setActivePage(userType === 'Admin' ? 'analytics' : 'whatsNew');
    }
  }, [location.search, userType, loading, isAuthenticated]);

  useEffect(() => { window.scrollTo(0, 0); }, [activePage]);

  const handleLogout = async () => {
    if (!api) return;
    try {
      const response = await api.post('/logout');
      if (response.status === 200) {
        setIsAuthenticated(false);
        setUsername('');
        setEmail('');
        setUserType('');
        history.push('/');
      }
    } catch (error) { console.error('Logout error:', error); }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleMenuItemClick = (page) => {
    setActivePage(page);
    setIsSidebarOpen(false);
    setIsMobileMenuOpen(false);
    history.push(`/dashboard?page=${page}`);
  };

  const renderActivePage = () => {
    switch (activePage) {
      case 'whatsNew': return <WhatsNew />;
      case 'analytics': return userType === 'Admin' ? <Analytics /> : <p>Access Denied.</p>;
      case 'viewOrder': return <ViewOrder />;
      case 'manageCode': return <ManageCode />;
      case 'manageProduct': return userType === 'Admin' ? <ManageProduct /> : <p>Access Denied.</p>;
      case 'manageDownload': return userType === 'Admin' ? <ManageDownload /> : <p>Access Denied.</p>;
      case 'manageUser': return userType === 'Admin' ? <ManageUser /> : <p>Access Denied.</p>;
      case 'maintenance': return userType === 'Admin' ? <Maintenance /> : <p>Access Denied.</p>;
      case 'settings': return <Settings />;
      case 'support': return <Support />;
      default: return userType === 'Admin' ? <Analytics /> : <WhatsNew />;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !hamburgerRef.current.contains(event.target)) setIsSidebarOpen(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && !fabButtonRef.current.contains(event.target)) setIsMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading || isAuthenticated === null) return <div className="loading-screen">Checking authentication...</div>;

  return (
    <div className={`dashboard ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="dashboard-header">
        <div className="hamburger_dashboard" onClick={toggleSidebar} ref={hamburgerRef}>☰</div>
        <h1>Dashboard</h1>
      </div>

      <aside ref={sidebarRef} className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <h2>Dashboard Menu</h2>
        <ul>
          <li onClick={() => handleMenuItemClick('whatsNew')} className={activePage === 'whatsNew' ? 'active' : ''}>What's New</li>
          <li onClick={() => handleMenuItemClick('viewOrder')} className={activePage === 'viewOrder' ? 'active' : ''}>View Order</li>
          <li onClick={() => handleMenuItemClick('manageCode')} className={activePage === 'manageCode' ? 'active' : ''}>Manage Code</li>
          {userType === 'Admin' && (
            <>
              <li onClick={() => handleMenuItemClick('analytics')} className={activePage === 'analytics' ? 'active' : ''}>Analytics</li>
              <li onClick={() => handleMenuItemClick('manageProduct')} className={activePage === 'manageProduct' ? 'active' : ''}>Manage Product</li>
              <li onClick={() => handleMenuItemClick('manageDownload')} className={activePage === 'manageDownload' ? 'active' : ''}>Manage Download</li>
              <li onClick={() => handleMenuItemClick('manageUser')} className={activePage === 'manageUser' ? 'active' : ''}>Manage User</li>
              <li onClick={() => handleMenuItemClick('maintenance')} className={activePage === 'maintenance' ? 'active' : ''}>Maintenance</li>
            </>
          )}
          <li onClick={() => handleMenuItemClick('settings')} className={activePage === 'settings' ? 'active' : ''}>Settings</li>
          <li onClick={() => handleMenuItemClick('support')} className={activePage === 'support' ? 'active' : ''}>Support</li>
          <li onClick={handleLogout} className="logout-button">Logout</li> 
        </ul>
      </aside>

      <div className="dashboard-welcome">
        <h1>Welcome <span className="username">{username?.toUpperCase()}</span>!</h1>
      </div>

      <main className="main-content">
        <button className="fab" onClick={toggleMobileMenu} ref={fabButtonRef}>+</button>
        {isMobileMenuOpen && (
          <div className="mobile-menu-dash" ref={mobileMenuRef}>
            <ul>
              <li onClick={() => handleMenuItemClick('whatsNew')}>What's New</li>
              <li onClick={() => handleMenuItemClick('viewOrder')}>View Order</li>
              <li onClick={() => handleMenuItemClick('manageCode')}>Manage Code</li>
              {userType === 'Admin' && (
                <>
                  <li onClick={() => handleMenuItemClick('analytics')}>Analytics</li>
                  <li onClick={() => handleMenuItemClick('manageProduct')}>Manage Product</li>
                  <li onClick={() => handleMenuItemClick('manageDownload')}>Manage Download</li>
                  <li onClick={() => handleMenuItemClick('manageUser')}>Manage User</li>
                  <li onClick={() => handleMenuItemClick('maintenance')}>Maintenance</li>
                </>
              )}
              <li onClick={() => handleMenuItemClick('settings')}>Settings</li>
              <li onClick={() => handleMenuItemClick('support')}>Support</li>
              <li onClick={handleLogout}>Logout</li>
            </ul>
          </div>
        )}
        {renderActivePage()}
      </main>
    </div>
  );
};

export default Dashboard;